import React, { useEffect } from 'react';

import { type Control, Controller, type UseFormSetValue, useWatch } from 'react-hook-form';

import { Checkbox, FormControlLabel, InputAdornment, Stack, TextField } from '@mui/material';

import type { FormValues } from './types';

interface SharpDropFieldsProps {
  control: Control<FormValues>;
  isSubmitting: boolean;
  setValue: UseFormSetValue<FormValues>;
}

export default function SharpDropFields({ control, isSubmitting, setValue }: SharpDropFieldsProps) {
  const prevCount = useWatch({ control, name: 'prevTotalInventoryCount' });
  const currentCount = useWatch({ control, name: 'totalInventoryCount' });

  useEffect(() => {
    if (prevCount != null && currentCount != null) {
      const pct = prevCount > 0 ? ((prevCount - currentCount) / prevCount) * 100 : 0;
      setValue('inventoryDropPct', Math.round(pct * 10) / 10);
    }
  }, [prevCount, currentCount, setValue]);

  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-start">
      <Controller
        name="prevTotalInventoryCount"
        control={control}
        rules={{ required: 'Required for sharp drop' }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            onChange={(e) => field.onChange(Number(e.target.value))}
            type="number"
            label="Previous Count"
            required
            disabled={isSubmitting}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{ minWidth: 160 }}
          />
        )}
      />
      <Controller
        name="totalInventoryCount"
        control={control}
        rules={{ required: 'Required for sharp drop' }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            onChange={(e) => field.onChange(Number(e.target.value))}
            type="number"
            label="Current Count"
            required
            disabled={isSubmitting}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{ minWidth: 160 }}
          />
        )}
      />
      <Controller
        name="inventoryDropPct"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            type="number"
            label="Drop %"
            disabled
            error={!!fieldState.error}
            helperText={fieldState.error?.message ?? 'Auto-calculated from counts'}
            sx={{ minWidth: 160 }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        )}
      />
      <Controller
        name="inventoryDropFlag"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox {...field} checked={field.value ?? true} disabled={isSubmitting} />}
            label="Drop Flag"
            sx={{ mt: 1 }}
          />
        )}
      />
    </Stack>
  );
}
